/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.formStats.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du chantier « formStats ». Fusionné dans `messagesFr`
 *   par `i18n/fr.ts`, donc utilisable exactement comme n'importe quelle clé.
 *
 * Pourquoi un fichier SÉPARÉ plutôt que d'allonger `fr.ts` :
 *   `fr.ts` fait ~6 700 lignes et c'est LA dépendance partagée du dépôt.
 *   Plusieurs chantiers menés en parallèle qui y écrivent en même temps se
 *   clobberaient — leçon du lot Console C9 : les agents parallèles ne se
 *   gênent pas sur LEURS fichiers, ils se gênent sur les fichiers COMMUNS.
 *   Un catalogue par chantier supprime la collision par construction.
 *
 * ⚠ Règles :
 *   • Wording français strict (§15.5), apostrophe typographique `’`.
 *   • JAMAIS le mot « atelier » en dur : employer les tokens du lexique
 *     (`{lexWorkshopPlural}`…) — cf. utils/eventLexicon.ts. Un test échoue
 *     sinon.
 *   • Aucune apostrophe DROITE juste avant un `{token}` (échappement ICU).
 *
 * @author Mikael
 * @since  2026-08-03 (lot CIRFA)
 */

export const messagesFormStatsFr: Record<string, string> = {
  // ── En-tête d’écran ────────────────────────────────────────────────────
  'formStats.title': 'Statistiques du formulaire',
  'formStats.lead':
    'Répartition des réponses au formulaire d’inscription, champ par champ. Les indicateurs sont construits à partir du formulaire publié : ajoutez un champ à réponses fermées, il apparaît ici.',

  // ── Sélecteur de rôle ──────────────────────────────────────────────────
  // Les deux rôles dont le nom est configurable par événement passent par le
  // lexique : le CIRFA parle d’unités, pas d’exposants.
  'formStats.role.label': 'Formulaire analysé',
  'formStats.role.visitor': '{lexVisitorPluralCap}',
  'formStats.role.exhibitor': '{lexExhibitorPluralCap}',
  'formStats.role.vip': 'Invités',
  'formStats.role.specific_public': 'Publics spécifiques',
  'formStats.role.student': 'Élèves',
  'formStats.role.child': 'Enfants',

  // ── États de chargement ────────────────────────────────────────────────
  'formStats.loading': 'Calcul des répartitions…',
  'formStats.error.title': 'Les statistiques n’ont pas pu être chargées.',
  'formStats.error.hint':
    'Le serveur n’a pas répondu, ou sa réponse n’a pas le format attendu. Réessayez ; si le problème persiste, vérifiez que la dernière version de l’administration est bien déployée.',
  'formStats.error.retry': 'Réessayer',

  // ── Vide ───────────────────────────────────────────────────────────────
  'formStats.empty.title': 'Aucun indicateur disponible pour ce formulaire.',
  'formStats.empty.hint':
    'Seuls les champs à réponses fermées produisent une répartition : listes déroulantes, boutons radio, choix multiples, cases à cocher et dates. Un champ de saisie libre donnerait autant de réponses différentes que de personnes inscrites.',

  // ── Synthèse ───────────────────────────────────────────────────────────
  'formStats.summary.rows':
    '{count, plural, =0 {Aucune inscription analysée} one {# inscription analysée} other {# inscriptions analysées}}',
  'formStats.summary.fields':
    '{count, plural, =0 {Aucun indicateur} one {# indicateur} other {# indicateurs}}',
  'formStats.summary.referenceYear': 'Âges calculés pour l’année {year}',
  'formStats.truncated.title': 'Lecture partielle',
  'formStats.truncated.text':
    'La lecture s’arrête à {limit, number} inscriptions. Les effectifs ci-dessous ne décrivent donc qu’une partie du public, pas la totalité.',

  // ── Carte d’un champ ───────────────────────────────────────────────────
  'formStats.field.synthetic': 'Donnée du compte',
  'formStats.field.syntheticHint':
    'Cet indicateur ne vient pas du formulaire : il est calculé à partir de l’année de naissance enregistrée sur le compte.',
  'formStats.field.multi': 'Plusieurs réponses possibles',
  'formStats.field.multiHint':
    'Une même personne peut cocher plusieurs réponses : le total des effectifs peut dépasser le nombre de répondants.',
  'formStats.field.respondents':
    '{count, plural, =0 {Aucun répondant} one {# répondant} other {# répondants}}',
  'formStats.field.answerRate': 'soit {rate} % des inscriptions analysées',
  'formStats.field.unanswered': 'Non renseigné',
  'formStats.field.noAnswer': 'Personne n’a encore répondu à ce champ.',
  'formStats.field.orphan': 'valeur retirée du formulaire',
  'formStats.field.type.select': 'Liste déroulante',
  'formStats.field.type.radio': 'Choix unique',
  'formStats.field.type.multiselect': 'Choix multiples',
  'formStats.field.type.checkbox': 'Case à cocher',
  'formStats.field.type.date': 'Date, regroupée par année',
  'formStats.field.type.age': 'Tranches d’âge',

  // ── Barres — texte lu par les lecteurs d’écran ─────────────────────────
  // Une largeur CSS n’est pas une information accessible : chaque barre porte
  // donc son effectif et son pourcentage en toutes lettres.
  'formStats.bar.aria':
    '{label} : {count, plural, one {# réponse} other {# réponses}}, soit {percent} % des répondants',
  'formStats.bar.ariaUnanswered':
    'Non renseigné : {count, plural, one {# inscription} other {# inscriptions}} sur {total, number}, soit {percent} %',

  // ── Modalités nommées par la plateforme ────────────────────────────────
  'formStats.value.other': 'Autres valeurs',
  'formStats.value.yes': 'Oui',
  'formStats.value.no': 'Non',

  // ── Tranches d’âge ─────────────────────────────────────────────────────
  'formStats.age.label': 'Âge',
  'formStats.age.under_15': 'Moins de 15 ans',
  'formStats.age.15_17': '15 à 17 ans',
  'formStats.age.18_20': '18 à 20 ans',
  'formStats.age.21_25': '21 à 25 ans',
  'formStats.age.26_35': '26 à 35 ans',
  'formStats.age.36_50': '36 à 50 ans',
  'formStats.age.over_50': 'Plus de 50 ans',

  // ── Export ─────────────────────────────────────────────────────────────
  'formStats.export.button': 'Exporter en CSV',
  'formStats.export.hint':
    'Le fichier reprend une ligne par modalité, non-répondants compris, avec les effectifs et les pourcentages. Il s’ouvre directement dans un tableur.',

  // ── Mentions ───────────────────────────────────────────────────────────
  'formStats.note.privacy':
    'Cet écran ne présente que des effectifs agrégés : aucun nom, aucune coordonnée. Une répartition portant sur très peu de répondants peut malgré tout permettre de reconnaître quelqu’un — restez prudent avant de la diffuser.',
};
